import React from "react";
import HeadingView from "common/views/heading/Heading.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { Fragment } from "core/components/FragmentProvider";
import Label from "core/components/label/Label";
import constants from "../constants";
import { Article } from "./entities";
import { Classes } from "@blueprintjs/core";
import { Form, Input } from "decorator-form";
import { Entity, Field } from "typegraph";
import { ID } from "common/entities/IEntity";
import InputType from "common/form/InputType";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import { EntityTable } from "core/components/EntityTable";
import IEntity from "core/util/IEntity";
import CountResult from "panels/organizationParticipant/entities/CountResult";
import entityMeta from "../entityMeta";
import HtmlDefault from "common/fragments/HtmlDefault";
import { NavLink } from "react-router-dom";
import ConfigUtil from "core/util/config/ConfigUtil";

@Entity(entityMeta.GLOSSARY_TERM)
@Form()
class GlossaryTerm implements IEntity {
	@Field()
	public id: ID;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Term",
		required: true,
	})
	public term: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Alternatives",
	})
	public alternatives: string;

	@Field()
	@Input({
		type: InputType.TEXT_HTML,
		title: "Definition",
		required: true,
	})
	public definition: string;
}

function mapQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

class GlossaryTermQuery implements IPageableListQuery<GlossaryTerm> {
	@Field({
		entity: GlossaryTerm,
		aliasFor: entityMeta.GLOSSARY_TERM.many,
		params: mapQueryParams,
	})
	public entities: null | GlossaryTerm[];

	@Field({
		entity: CountResult,
		aliasFor: "glossaryTerm_count",
		params: mapQueryParams,
	})
	public count: CountResult;
}

interface IArticleEditFragmentProps {
	article: Article;
}

export const displayName: Fragment<IArticleEditFragmentProps> = {
	name: "Display Name",
	component: (props) => (
		<HeadingView viewKey="article.displayName">
			{props.article.displayName}
		</HeadingView>
	),
};

export const tags: Fragment<IArticleEditFragmentProps> = {
	name: "Tags",
	enabled: (props) => !!props.article.tags && props.article.tags.length > 0,
	component: (props) => (
		<div>
			<HeadingView viewKey="property.title">
				<Label k="tags" v="Tags" />
			</HeadingView>
			{props.article.tags.reduce((lst, tag, i) => {
				if (i > 0) {
					lst.push(
						<React.Fragment key={tag.id + "-separator"}>
							{", "}
						</React.Fragment>
					);
				}

				lst.push(
					<PageLinkView
						key={tag.id}
						viewKey="tag.link"
						preserveParams={false}
						params={{ [constants.PARAM_TAG_IDS]: tag.id }}
					>
						<Label k={`Tag.${tag.id}.name`} v={tag.name} />
					</PageLinkView>
				);

				return lst;
			}, [])}
		</div>
	),
};

export const glossaryList: Fragment<IArticleEditFragmentProps> = {
	name: "Glossary List",
	component: (props) => {
		const params = {};

		if (props.article && props.article.id) {
			params["articleId"] = props.article.id;
			params["sort"] = "term";
		}

		const appPath = ConfigUtil.getEnvConfig().appPath;
		let path = "";
		if (appPath) {
			path = appPath + "glossary#";
		}

		return (
			<EntityTable
				pagingQuery={GlossaryTermQuery}
				formClass={GlossaryTerm}
				params={params}
				canCreate={false}
				canDelete={false}
				canUpdate={false}
				showPaginationOptions={true}
				columns={[
					{
						key: "term",
						label: "Term",
						sort: "term",
						renderCell: ({ data }) => {
							return (
								<PageLinkView
									viewKey="glossaryTerm.link"
									preserveParams={false}
									params={{
										[constants.PARAM_TERM_ID]: data.id,
									}}
								>
									<NavLink
										to={
											path +
											"" +
											data.term[0].toUpperCase()
										}
									>
										<HtmlDefault html={data.term} />
										{data.alternatives && (
											<p className={Classes.TEXT_MUTED}>
												{`Alternatives: ${data.alternatives}`}
											</p>
										)}
									</NavLink>
								</PageLinkView>
							);
						},
					},
					{
						key: "definition",
						label: "Definition",
						sort: "term",
						renderCell: ({ data }) => {
							return (
								<PageLinkView
									viewKey="glossaryDefinition.link"
									preserveParams={false}
									params={{
										[constants.PARAM_TERM_ID]: data.id,
									}}
								>
									<HtmlDefault html={data.definition} />
								</PageLinkView>
							);
						},
					},
				]}
			/>
		);
	},
};



// WEBPACK FOOTER //
// ./src/panels/articleDetails/article/glossaryFragments.tsx